/**
 * The live scene's renderer, offered to the Edit flight (`spec-onboarding-persistent-scene-edit-flight`,
 * R4.3): the see-through copies of the fading floors are built with `gl.compile` on the press, before
 * the camera moves, so no shader program compiles during the flight (criterion 5).
 *
 * They are built for a render target, not the screen. The live twin always draws through its
 * post-processing composer (`MachineLookPostProcessChain` in `TwinScene`), so every frame of the flight
 * renders the scene into the composer's buffer, in linear colour with no tone mapping. A program built
 * for the screen (sRGB, tone-mapped) is a different program, unused, and the one the frame needs would
 * still compile mid-flight: measured on Finsbury Park, two machine-look programs 90 ms in.
 *
 * In development builds only, `window.__editFlight` reads the scene for the rig: the height scale, the
 * machine look's mix, the programs built so far, the live floors and the anchor Edit flies from.
 * Nothing in production.
 */
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Object3D, WebGLRenderTarget } from 'three';
import { useMachineLook } from './MachineLook';
import {
  liveAnchor,
  liveEditFlightScene,
  liveFloorBids,
  liveFloors,
  registerLiveFloorCollector,
  registerLiveWarm,
} from './liveEditFlightScene';

const DEV = process.env.NODE_ENV !== 'production';

export const LiveEditFlightBridge = () => {
  const gl = useThree(s => s.gl);
  const scene = useThree(s => s.scene);
  const camera = useThree(s => s.camera);
  const look = useMachineLook();

  useEffect(
    () =>
      registerLiveWarm(() => {
        const previous = gl.getRenderTarget();
        const target = new WebGLRenderTarget(1, 1);
        try {
          gl.setRenderTarget(target);
          gl.compile(scene, camera);
        } catch {
          // A renderer that cannot compile now builds as it draws, as before.
        } finally {
          gl.setRenderTarget(previous);
          target.dispose();
        }
      }),
    [gl, scene, camera],
  );

  useEffect(() => registerLiveFloorCollector(() => sceneFloors(scene)), [scene]);

  useEffect(() => {
    if (!DEV) return undefined;
    const w = window as unknown as { __editFlight?: unknown };
    w.__editFlight = {
      heightScale: () => liveEditFlightScene.getHeightScale(),
      state: () => liveEditFlightScene.getState(),
      lookMix: () => look?.getMix() ?? null,
      programs: () => (gl.info as { programs?: unknown[] }).programs?.length ?? 0,
      programNames: () =>
        ((gl.info as { programs?: Array<{ name: string; cacheKey: string }> }).programs ?? []).map(
          p => `${p.name}:${p.cacheKey}`,
        ),
      floors: () => liveFloorBids(),
      visibleFloors: () =>
        liveFloors()
          .filter(([, g]) => isDrawn(g))
          .map(([bid]) => bid),
      anchor: () => liveAnchor(),
    };
    return () => {
      if (w.__editFlight) delete w.__editFlight;
    };
  }, [gl, scene, look]);

  return null;
};

/**
 * The floor groups under `scene`, by the BID each `Floor` names in `userData.floorBid` (R4.3). The
 * search stops at a floor: floors do not nest.
 */
export const sceneFloors = (scene: Object3D): [string, Object3D][] => {
  const found: [string, Object3D][] = [];
  const visit = (o: Object3D) => {
    const bid = (o.userData as { floorBid?: unknown }).floorBid;
    if (typeof bid === 'string' && bid !== '') {
      found.push([bid, o]);
      return;
    }
    o.children.forEach(visit);
  };
  visit(scene);
  return found;
};

/** Is `o` drawn: it and every ancestor visible, and some mesh under it visible and not fully clear? */
const isDrawn = (o: import('three').Object3D): boolean => {
  for (let p: import('three').Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  let drawn = false;
  o.traverse(c => {
    const mesh = c as import('three').Mesh;
    if (drawn || !mesh.isMesh || !mesh.visible) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    if (mats.some(m => m && m.opacity > 0)) drawn = true;
  });
  return drawn;
};
